/**
 * Feuille de brouillon — l'entrée dans la carte de la question : le titre
 * « Ma feuille de brouillon », une aide « ? » (FAQ `brouillon`), et un APERÇU
 * de la feuille (quadrillage + marge rouge) qui l'ouvre au toucher. Sous une
 * question déjà jouée, `ScratchLinesPreview` montre les lignes écrites.
 */

import './scratch-sheet.css';
import { useState } from 'react';
import { PencilLine, StepForward } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { HelpTip } from '@/components/help-tip';
import { programOf, type ScratchSession } from './scratch-session';
import { useSessionState } from './scratch-sheet';
import { MiniLines } from './sheet-paper';
import { MathListsContext } from './math-view';
import { FREE_SHEET_NOTE } from './free-sheet';
import { ProgramSimulator } from './program-simulator';

export function ScratchEntryCard({
  session,
  onOpen,
  draft,
  answered = false,
}: {
  session: ScratchSession;
  onOpen: () => void;
  draft?: boolean;
  /** question répondue : la feuille se relit (rien à proposer si elle est vide) */
  answered?: boolean;
}) {
  const state = useSessionState(session);
  if (answered && state.lines.length === 0) return null;
  const steps = state.lines.filter((l) => l.kind === 'step').length;
  const n = state.lines.length;
  const summary = !n
    ? 'feuille vide'
    : session.free
      ? `${n} ligne${n > 1 ? 's' : ''}, sans HORA`
      : `${steps} ligne${steps > 1 ? 's' : ''} validée${steps > 1 ? 's' : ''}, +${state.hora} HORA`;
  return (
    <div className="sc-root flex w-full flex-col gap-2 rounded-2xl border border-[var(--sc-line)] bg-[var(--sc-surface)] p-3">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--sc-gold-soft)] text-[var(--sc-gold-ink)]">
          <PencilLine className="h-5 w-5" aria-hidden />
        </span>
        <strong className="font-display flex-1 text-[15px] font-bold text-[var(--sc-ink)]">
          Ma feuille de brouillon
          {draft && (
            <span className="ml-2 align-middle text-xs font-semibold text-[var(--sc-muted)]">
              (aperçu)
            </span>
          )}
        </strong>
        <HelpTip topic="brouillon" label="la feuille de brouillon" />
      </div>
      {session.free && (
        <p className="text-[13px] text-[var(--sc-muted)]" data-testid="scratch-free-entry-note">
          {FREE_SHEET_NOTE}
        </p>
      )}
      <button
        type="button"
        onClick={onOpen}
        data-testid="scratch-entry"
        aria-label={`${answered ? 'Relire' : 'Ouvrir'} ma feuille de brouillon (${summary})`}
        className="sc-entry-paper block w-full rounded-xl py-2.5 pl-8 pr-3 text-left transition-transform active:scale-[.98]"
      >
        {state.lines.length > 0 ? (
          <MathListsContext.Provider value={!session.free && !!programOf(session.model)}>
            <MiniLines state={state} />
          </MathListsContext.Provider>
        ) : (
          <span className="block text-[14px] text-[var(--sc-muted)]">
            Ta feuille est vide pour l’instant.
          </span>
        )}
      </button>
    </div>
  );
}

/** Lignes écrites sur la feuille d'une question déjà jouée (rien si elle est vide). */
export function ScratchLinesPreview({
  session,
  className,
}: {
  session: ScratchSession | null;
  className?: string;
}) {
  if (!session) return null;
  return <ScratchLinesPreviewInner session={session} className={className} />;
}

function ScratchLinesPreviewInner({
  session,
  className,
}: {
  session: ScratchSession;
  className?: string;
}) {
  const state = useSessionState(session);
  // question jouée, programme Python : le simulateur pas à pas (piste I1, moteur v29)
  const program = session.free ? null : programOf(session.model);
  const [simOpen, setSimOpen] = useState(false);
  if (state.lines.length === 0 && !program) return null;
  return (
    <div className={cn('sc-root', className)} data-testid="scratch-history">
      {state.lines.length > 0 && (
        <>
          <p className="mb-1 font-sans text-xs font-semibold text-[var(--sc-muted)]">
            {session.free ? 'Mon brouillon · sans HORA' : `Mon brouillon · +${state.hora} HORA`}
          </p>
          <div className="sc-entry-paper rounded-xl py-2 pl-8 pr-3">
            <MathListsContext.Provider value={!!program}>
              <MiniLines state={state} max={null} />
            </MathListsContext.Provider>
          </div>
        </>
      )}
      {program && (
        <button
          type="button"
          className="sc-sim-open mt-2"
          data-testid="scratch-history-simulate"
          onClick={() => setSimOpen(true)}
        >
          <StepForward className="h-4 w-4" aria-hidden />
          Voir le programme s’exécuter pas à pas
        </button>
      )}
      {simOpen && program && (
        <ProgramSimulator program={program} onClose={() => setSimOpen(false)} />
      )}
    </div>
  );
}
